import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfDay, startOfWeek, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface TemporalDataPoint {
  period: string;
  averageRating: number;
  totalResponses: number;
  sortDate: Date;
}

export type PeriodType = 'day' | 'week' | 'month';

export const useCSATTemporalEvolution = (
  daysRange: number = 30,
  periodType: PeriodType = 'day'
) => {
  return useQuery({
    queryKey: ['csat-temporal-evolution', daysRange, periodType],
    queryFn: async (): Promise<TemporalDataPoint[]> => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysRange);

      const { data, error } = await supabase
        .from('csat_responses')
        .select('rating, created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!data || data.length === 0) {
        return [];
      }

      // Agrupar por período
      const periodData: Record<string, { ratings: number[]; sortDate: Date }> = {};

      data.forEach(response => {
        const date = new Date(response.created_at);
        let periodKey: string;
        let periodStart: Date;

        switch (periodType) {
          case 'day':
            periodStart = startOfDay(date);
            periodKey = format(periodStart, 'dd/MM/yy', { locale: ptBR });
            break;
          case 'week':
            periodStart = startOfWeek(date, { weekStartsOn: 1 });
            const weekEnd = new Date(periodStart.getTime() + 6 * 24 * 60 * 60 * 1000);
            periodKey = `${format(periodStart, 'dd/MM', { locale: ptBR })}-${format(weekEnd, 'dd/MM', { locale: ptBR })}`;
            break;
          case 'month':
            periodStart = startOfMonth(date);
            periodKey = format(periodStart, 'MMM/yy', { locale: ptBR });
            break;
          default:
            periodStart = startOfDay(date);
            periodKey = format(periodStart, 'dd/MM/yy', { locale: ptBR });
        }

        if (!periodData[periodKey]) {
          periodData[periodKey] = {
            ratings: [],
            sortDate: periodStart,
          };
        }

        periodData[periodKey].ratings.push(response.rating);
      });

      // Calcular médias e converter para array
      return Object.entries(periodData)
        .map(([period, data]) => {
          const averageRating = data.ratings.reduce((sum, r) => sum + r, 0) / data.ratings.length;
          return {
            period,
            averageRating: Math.round(averageRating * 10) / 10,
            totalResponses: data.ratings.length,
            sortDate: data.sortDate,
          };
        })
        .sort((a, b) => a.sortDate.getTime() - b.sortDate.getTime());
    },
  });
};

